import React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import Button from "../UI/Button/Button.jsx";
import SelectFilter from "../UI/Select/SelectFilter.tsx";

/* ---------- типы полей ---------- */
type Base = {
  /** Текст‑лейбл сверху */
  label: string;
};

export type FieldConfig =
  | ({
      type: "select" | "text" | "date";
      key: string;
      /** только для select */
      options?: { value: string | number; label: string }[];
    } & Base)
  | ({
      type: "dateRange";
      keyFrom: string;
      keyTo: string;
    } & Base)
  | ({
      type: "group";              // «стек» : одна подпись, несколько вложенных полей
      children: FieldConfig[];
    } & Base);

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (f: Record<string, any>) => void;
  onReset: () => void;
  fields: FieldConfig[];
  filters: Record<string, any>;
  setFilters: React.Dispatch<React.SetStateAction<Record<string, any>>>;
}

const FilterDialog: React.FC<Props> = ({
  open,
  onClose,
  onApply,
  onReset,
  fields,
  filters,
  setFilters,
}) => {
  const { t } = useTranslation();
  const setVal = (k: string, v: any) => setFilters((p) => ({ ...p, [k]: v }));

  /* ---------- рендер одиночного поля ---------- */
  const renderSingle = (f: FieldConfig) => {
    if (f.type === "select")
      return (
        <SelectFilter
          value={filters[f.key] ?? ""}
          onChange={(e) => setVal(f.key, e.target.value)}
        >
          <option value="">{t("Любой")}</option>
          {f.options?.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </SelectFilter>
      );

    if (f.type === "text" || f.type === "date")
      return (
        <TextField
          type={f.type === "date" ? "date" : "text"}
          value={filters[f.key] ?? ""}
          onChange={(e) => setVal(f.key, e.target.value)}
          size="small"
          fullWidth
          InputLabelProps={{ shrink: f.type === "date" }}
        />
      );

    if (f.type === "dateRange")
      return (
        <Box display="flex" gap={1} alignItems="center">
          <TextField
            type="date"
            value={filters[f.keyFrom] ?? ""}
            onChange={(e) => setVal(f.keyFrom, e.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
            —
          </Typography>
          <TextField
            type="date"
            value={filters[f.keyTo] ?? ""}
            onChange={(e) => setVal(f.keyTo, e.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      );

    return null;
  };

  /* ---------- рендер любого FieldConfig ---------- */
  const renderField = (f: FieldConfig) => {
    if (f.type !== "group") {
      return (
        <Box mb={3}>
          <Typography variant="body2" mb={0.5} fontWeight={500}>
            {f.label}
          </Typography>
          {renderSingle(f)}
        </Box>
      );
    }

    /* GROUP: один label, внутри вертикально несколько дочерних */
    return (
      <Box mb={3}>
        <Typography variant="body2" mb={0.5} fontWeight={500}>
          {f.label}
        </Typography>

        {f.children.map((child, idx) => (
          <Box key={idx} mb={idx === f.children.length - 1 ? 0 : 2}>
            {renderSingle(child)}
          </Box>
        ))}
      </Box>
    );
  };

  /* ---------- Drawer ---------- */
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 420,
          p: 0,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box position="relative" px={6} pt={4} pb={3}>
        <Typography variant="h6" fontWeight={600}>
          {t("Фильтрация")}
        </Typography>

        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            position: "absolute",
            left: -20,
            top: 70,
            width: 41,
            height: 41,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Divider />

      {/* Content */}
      <Box flex={1} overflow="hidden auto" px={6} py={3}>
        {fields.map((f, i) => (
          <React.Fragment key={i}>{renderField(f)}</React.Fragment>
        ))}
      </Box>

      <Divider />
      {/* Footer */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={6}
        py={3}
      >
        <Button variant="primary" onClick={() => onApply(filters)}>
          {t("Применить")}
        </Button>
        <Button variant="secondary" onClick={onReset} style={{ minWidth: 140 }}>
          {t("Очистить")}
        </Button>
      </Box>
    </Drawer>
  );
};

export default FilterDialog;
